// Joana chat shell — the trusted surface driver. Owns a ViewHost + a Renderer and
// turns state into paint: build the catalog tree, diff it, apply the frame. It's
// the SAME ViewHost/Renderer the mini-app host uses (joana/worker), but driven
// directly — the chat shell is first-party + trusted, so it builds trees in-thread
// with no isolate/sandbox between it and the renderer (that boundary is only for
// untrusted mini-apps, per ADR-0011).
//
// Renderer events (taps, input) are decoded back into the `shell` action that the
// view bound, and dispatched with the merged action + event payload.

import type { VNode } from "../render/catalog.ts";
import type { RenderEvent } from "../render/patch.ts";
import { applyFrame, type Renderer } from "../render/renderer.ts";
import { ViewHost } from "../render/view-host.ts";
import type { ShellState } from "./store.ts";

export type RendererFactory = (onEvent: (e: RenderEvent) => void) => Renderer;
export type ActionHandler = (cmd: string, payload: Record<string, unknown>) => void;

export class Surface {
  private readonly host = new ViewHost();
  private readonly renderer: Renderer;

  private readonly buildTree: (s: ShellState) => VNode;
  private readonly onAction: ActionHandler;

  constructor(
    rendererFactory: RendererFactory,
    buildTree: (s: ShellState) => VNode,
    onAction: ActionHandler,
  ) {
    this.buildTree = buildTree;
    this.onAction = onAction;
    this.renderer = rendererFactory((e) => this.handleEvent(e));
  }

  /** Re-render the whole surface from state (pure tree → diff → patch frame). */
  render(state: ShellState): void {
    applyFrame(this.renderer, this.host.render(this.buildTree(state)));
  }

  private handleEvent(e: RenderEvent): void {
    const action = this.host.actionFor(e);
    if (!action || action.intent !== "shell") return;
    const payload = { ...(action.payload ?? {}), ...(e.payload ?? {}) } as Record<string, unknown>;
    const cmd = String(payload.cmd ?? "");
    if (cmd) this.onAction(cmd, payload);
  }
}
